/**
 * ISSUE-040 — the single sink for UNEXPECTED errors.
 *
 * `ErrorBoundary` catches render/commit-phase throws only. Everything else —
 * event handlers, `setTimeout`/`requestAnimationFrame` callbacks, every
 * `await` continuation and every unhandled promise rejection — used to vanish
 * without a trace. This module records those, once each, on the console.
 *
 * SCOPE. "Unexpected" means: no existing code path already writes a FAILED
 * status or shows a message for it. Upload and job failures are NOT
 * unexpected — `setFailed` (SourcePanel, TargetPanel, useResultUpload,
 * AudioEditorPanel, MSSSourceList) and `markFailed` in useProcessingEvents
 * already surface them as "Uploading Failed" / "Generating Failed" through the
 * fetchStatus selectors. Do not add a second, global voice for those.
 *
 * Two failures that USED to land here now have their own voice as well, and
 * are out of scope for the same reason: a refused `POST /resources`
 * (`useUploadSlots`) and a refused `POST /cvc` / `POST /mss` (`useJobSubmit`).
 * Both used to reach this sink only as an unhandled rejection — which is
 * precisely why they needed handling: those two fail BEFORE any row exists to
 * carry a FAILED badge, so the redacted record written here was the only trace
 * either of them left. They are now acknowledged in a modal and logged with
 * their own console.error at the call site.
 *
 * DESTINATION. The console, and nothing else. There is no error-monitoring
 * service in this app and this issue adds none: `src/no-analytics.test.ts`
 * bars third-party SDKs, and the static guards in `errorSink.test.ts` bar a
 * network call and any import at all from this file. `console.error` — not
 * `console.log` — because production builds run
 * babel-plugin-transform-remove-console with `exclude: ['error', 'warn']`
 * (craco.config.js), so a `console.log` sink would be stripped out of the
 * production bundle and this whole module would ship as a no-op.
 *
 * DISCLOSURE (NFR-011). An axios rejection carries `config.url` — an S3
 * presigned URL with `X-Amz-Signature` and `X-Amz-Credential` — plus
 * `config.headers` (`x-session-id`) and `config.data`, which for a presigned
 * PUT is the uploaded audio itself. `error.response.config`, `error.request`
 * and `error.toJSON()` all repeat the same values. So the payload is built by
 * CONSTRUCTION from a four-key allowlist rather than by deleting fields from
 * the error: a field a future axios version adds cannot leak by default.
 * An allowlist alone is not enough, though — `message` and `stack` are on it
 * and a URL can be interpolated straight into either — so both are scrubbed
 * before they are emitted.
 *
 * This module imports nothing, deliberately: with no transitive graph it
 * cannot reach a network client or the model catalog (NFR-019).
 */

/** Marks every record this module writes, so tests can count its own output. */
export const ERROR_SINK_PREFIX = '[unexpected error]';

/** The only shape ever emitted. Adding a key here widens the disclosure. */
export interface UnexpectedErrorReport {
  name: string;
  message: string;
  stack: string;
  context: string;
}

const REDACTED = '[redacted]';
const NON_ERROR_NAME = 'NonError';
const DEFAULT_CONTEXT = 'unspecified';

const CONTEXT_WINDOW_ERROR = 'window error event';
const CONTEXT_UNHANDLED_REJECTION = 'unhandled promise rejection';

/**
 * A stack frame's `:line:column` suffix, kept when the URL around it is
 * redacted: positions are not sensitive, and without them a redacted frame
 * cannot be resolved through a source map at all.
 */
const SOURCE_POSITION = /:\d+:\d+$/;

const redactUrl = (url: string): string => {
  const position = url.match(SOURCE_POSITION);
  return position ? REDACTED + position[0] : REDACTED;
};

/**
 * Redactions applied to every string that survives into the payload. A
 * denylist is the wrong tool for object FIELDS (hence the allowlist above),
 * but free text is the one place it is the only tool.
 */
const REDACTIONS: { pattern: RegExp; replace: (match: string) => string }[] = [
  {
    // Any absolute URL. Presigned S3 links carry the signature, the credential
    // scope and the bucket host, and a URL is how all of them reach a message
    // or a stack frame.
    pattern: /\b[a-z][a-z0-9+.-]*:\/\/[^\s'"`<>()[\]{},;]+/gi,
    replace: redactUrl,
  },
  {
    // SigV4 query/header parameters and the session header, with or without a
    // glued value — the key names alone identify the request.
    pattern:
      /\b(?:x-amz-[a-z0-9-]*|x-session-id)\b\s*[:=]?\s*[^\s'"`<>()[\]{},;&]*/gi,
    replace: () => REDACTED,
  },
  {
    // `Authorization: Bearer <token>`, or a bare bearer token.
    pattern: /\b(?:authorization\s*[:=]\s*)?bearer\s+[^\s'"`<>()[\]{},;]+/gi,
    replace: () => REDACTED,
  },
  {
    // An AWS access key id, which can appear on its own.
    pattern: /\bAKIA[0-9A-Z]{4,}\b/g,
    replace: () => REDACTED,
  },
  {
    // Anything else named as a credential and given a value. Deliberately
    // requires the `=` or `:`, so ordinary prose ("unexpected token <") is
    // left readable.
    pattern:
      /\b(?:signature|credential|token|api[_-]?key|password|secret|session[_-]?id)\b\s*[:=]\s*[^\s'"`<>()[\]{},;&]+/gi,
    replace: () => REDACTED,
  },
];

/**
 * WHAT THIS DENYLIST CANNOT SEE — enumerated because a negative guard that
 * does not name its blind routes reads as a guarantee it never made. Measured
 * against 24 payloads during the ISSUE-040 review (PR #459); these 9 survive:
 *
 *  1. A URL with no scheme. The URL pattern anchors on `scheme://`, so
 *     `//bucket.s3.amazonaws.com/key?...` (protocol-relative) and
 *     `bucket.s3.amazonaws.com/key?sig=...` keep host, bucket and key. The
 *     parameter rules still redact `X-Amz-*`, but an abbreviated name (`sig`)
 *     matches none of them.
 *  2. A signature in the PATH rather than a parameter — a bare SigV4 hex run
 *     is indistinguishable from any other hex.
 *  3. A credential with no key name beside it: a raw JWT, a bare session
 *     UUID, an AWS secret key (the `AKIA` rule covers only the ACCESS key),
 *     or `apiKey abc123` with a space where rule 5 requires `=` or `:`.
 *  4. A model identifier. NFR-019's guard covers `context` and the module's
 *     own imports; a model name that reaches the sink INSIDE an error message
 *     is not redacted here. Widening this pattern would mean compiling model
 *     names into the bundle, which is the very thing NFR-019 forbids — so the
 *     control for this class is upstream: do not interpolate a model
 *     identifier into an Error message.
 *
 * None of the nine is producible by code in this repo today (every `new Error`
 * site carries a literal message, axios messages carry no URL, and the auth
 * header was removed at transfer), which is why they are documented rather
 * than chased with a wider regex. Re-measure if that stops being true.
 */
const scrub = (value: string): string =>
  REDACTIONS.reduce(
    (text, { pattern, replace }) => text.replace(pattern, replace),
    value
  );

/**
 * Describes a non-Error value without enumerating its fields: `String({})` is
 * `'[object Object]'`, never the object's contents.
 */
const describeValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return String(value);
};

const buildReport = (
  error: unknown,
  context: string
): UnexpectedErrorReport => {
  const source =
    typeof error === 'object' && error !== null
      ? (error as Record<string, unknown>)
      : {};
  return {
    name: scrub(typeof source.name === 'string' ? source.name : NON_ERROR_NAME),
    message: scrub(
      typeof source.message === 'string' ? source.message : describeValue(error)
    ),
    stack: scrub(typeof source.stack === 'string' ? source.stack : ''),
    context: scrub(context),
  };
};

/**
 * The last reported value, so one incident reported twice in a row is one
 * record. React's development build reports a throwing event handler to
 * `window` TWICE for a single throw (the invokeGuardedCallback fake dispatch,
 * then the rethrow); both carry the same Error object.
 *
 * Object identity AND context: two rejections carrying the same string are two
 * failures, and a repeat that is not consecutive is a recurrence worth seeing.
 * The context is part of the key because the same Error object reaching the
 * sink from two different call sites is two facts, and keeping only the first
 * provenance is the more misleading of the two records to drop.
 */
let lastReported: { value: unknown; context: string } | null = null;

/**
 * Records an error that no other code path surfaces. Never throws: it runs
 * inside the `window` error listener, where a throw would fire another error
 * event and recurse.
 */
export const reportUnexpectedError = (
  error: unknown,
  context: string = DEFAULT_CONTEXT
): void => {
  if (
    typeof error === 'object' &&
    error !== null &&
    lastReported !== null &&
    lastReported.value === error &&
    lastReported.context === context
  ) {
    return;
  }
  lastReported = { value: error, context };
  try {
    console.error(ERROR_SINK_PREFIX, buildReport(error, context));
  } catch {
    // Reading the error threw (a hostile getter, a revoked proxy). Report the
    // failure to report rather than letting it escape.
    console.error(
      ERROR_SINK_PREFIX,
      buildReport(new Error('error could not be read'), DEFAULT_CONTEXT)
    );
  }
};

const onWindowError = (event: ErrorEvent): void => {
  // `event.error` is the thrown value; a cross-origin script error arrives
  // with it nulled, leaving the message as the only description.
  reportUnexpectedError(event.error ?? event.message, CONTEXT_WINDOW_ERROR);
};

const onUnhandledRejection = (event: PromiseRejectionEvent): void => {
  reportUnexpectedError(event.reason, CONTEXT_UNHANDLED_REJECTION);
};

let installed = false;

/**
 * Installs the two global listeners, once per page load.
 *
 * Called from module scope in `src/index.tsx`, before `root.render`, NOT from
 * a component effect: `<React.StrictMode>` mounts every effect twice in
 * development, so an effect-based install would register both listeners twice
 * and record every error twice, and it would miss anything thrown during the
 * first render pass.
 *
 * Neither handler calls `preventDefault()`. That is deliberate: the sink ADDS
 * a scrubbed record, it does not replace the platform's own uncaught-error
 * report, which is the only unminified stack a developer has in devtools and
 * which no library can suppress for the Network panel anyway. "Recorded
 * exactly once" is therefore a property of this module's own output, which the
 * tests measure by ERROR_SINK_PREFIX.
 */
export const installGlobalErrorSink = (): void => {
  if (installed) return;
  installed = true;
  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
};

/**
 * Detaches the listeners and clears the duplicate-suppression slot, so a test
 * file can install a fresh sink per case (same pattern as
 * `resetRuntimeConfigForTests`).
 */
export const resetGlobalErrorSinkForTests = (): void => {
  window.removeEventListener('error', onWindowError);
  window.removeEventListener('unhandledrejection', onUnhandledRejection);
  installed = false;
  lastReported = null;
};
